/**
 * FormFlu Login Page
 * 
 * Handles the complete authentication flow:
 * 1. Email/password login
 * 2. MFA verification (or setup for new users)
 * 3. Recovery code display (after MFA setup)
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts';
import './LoginPage.css';

export function LoginPage() {
  const navigate = useNavigate();
  const {
    step,
    isAuthenticated,
    isLoading,
    error,
    mfaSetupData,
    recoveryCodes,
    login,
    verifyMfa,
    useRecoveryCode,
    startMfaSetup,
    completeMfaSetup,
    acknowledgeRecoveryCodes,
    clearError,
  } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Auto-fetch MFA setup data when entering setup step
  useEffect(() => {
    if (step === 'mfa_setup' && !mfaSetupData) {
      startMfaSetup();
    }
  }, [step, mfaSetupData, startMfaSetup]);

  // Handle login form submit
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);
    
    // Basic validation
    if (!email.trim()) {
      setLocalError('Please enter your email address');
      return;
    }
    
    if (!password) {
      setLocalError('Please enter your password');
      return;
    }
    
    await login({ email, password });
  };

  // Handle MFA verification
  const handleMfaVerify = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (showRecoveryInput) {
      await useRecoveryCode(recoveryCode);
    } else {
      await verifyMfa(mfaCode);
    }
  };

  // Handle MFA setup completion
  const handleMfaSetup = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (mfaSetupData) {
      await completeMfaSetup(mfaCode, mfaSetupData.secret);
    }
  };

  // Copy recovery codes to clipboard
  const handleCopyRecoveryCodes = async () => {
    const codesText = recoveryCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  // Render based on current step
  const renderContent = () => {
    switch (step) {
      case 'idle':
      case 'credentials':
        return (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-header">
              <Link to="/" className="logo-link">
                <img 
                  src="/images/finalformflumainlogo.png" 
                  alt="FormFlu - Back to Home" 
                  className="login-logo" 
                />
              </Link>
              <h1>Welcome back</h1>
              <p>Sign in to your FormFlu account</p>
            </div>

            {(error || localError) && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error || localError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>

            <div className="login-footer">
              <a href="/forgot-password">Forgot password?</a>
              <p className="signup-prompt">Don't have an account? <Link to="/register">Sign up</Link></p>
            </div>
          </form>
        );

      case 'mfa_verify':
        return (
          <form onSubmit={handleMfaVerify} className="login-form">
            <div className="form-header">
              <div className="mfa-icon">🔐</div>
              <h1>Two-factor authentication</h1>
              <p>
                {showRecoveryInput
                  ? 'Enter one of your recovery codes'
                  : 'Enter the 6-digit code from your authenticator app'}
              </p>
            </div>

            {error && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {!showRecoveryInput ? (
              <div className="form-group">
                <label htmlFor="mfaCode">Verification code</label>
                <input
                  type="text"
                  id="mfaCode"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  className="mfa-input"
                  maxLength={6}
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="recoveryCode">Recovery code</label>
                <input
                  type="text"
                  id="recoveryCode"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  required
                  autoFocus
                  className="recovery-input"
                />
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary btn-full"
              disabled={isLoading || (!showRecoveryInput && mfaCode.length !== 6)}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>

            <div className="login-footer">
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setShowRecoveryInput(!showRecoveryInput);
                  setMfaCode('');
                  setRecoveryCode('');
                  clearError();
                }}
              >
                {showRecoveryInput 
                  ? 'Use authenticator app instead' 
                  : 'Use a recovery code instead'}
              </button>
            </div>
          </form>
        );

      case 'mfa_setup':
        return (
          <form onSubmit={handleMfaSetup} className="login-form mfa-setup-form">
            <div className="form-header">
              <div className="mfa-icon">📱</div>
              <h1>Set up two-factor authentication</h1>
              <p>
                Scan the QR code with your authenticator app
                (Google Authenticator, Authy, etc.)
              </p>
            </div>

            {error && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {mfaSetupData ? (
              <>
                <div className="qr-code-container">
                  <img 
                    src={mfaSetupData.qr_code} 
                    alt="Scan this QR code with your authenticator app"
                    className="qr-code"
                  />
                </div>

                <div className="manual-entry">
                  <p>Can't scan? Enter this code manually:</p>
                  <code className="secret-code">{mfaSetupData.secret}</code>
                </div>

                <div className="form-group">
                  <label htmlFor="setupCode">Enter verification code</label>
                  <input
                    type="text"
                    id="setupCode"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoComplete="one-time-code"
                    className="mfa-input"
                    maxLength={6}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-full"
                  disabled={isLoading || mfaCode.length !== 6}
                >
                  {isLoading ? 'Verifying...' : 'Verify and enable'}
                </button>
              </>
            ) : (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading setup data...</p>
              </div>
            )}
          </form>
        );

      case 'recovery_codes':
        return (
          <div className="login-form recovery-codes-form">
            <div className="form-header">
              <div className="mfa-icon success">✅</div>
              <h1>Save your recovery codes</h1>
              <p>
                Store these codes in a safe place. You can use them to access
                your account if you lose your authenticator device.
              </p>
            </div>

            <div className="warning-message">
              <span className="warning-icon">⚠️</span>
              <strong>Important:</strong> These codes will only be shown once.
              Make sure to save them now!
            </div>

            <div className="recovery-codes-grid">
              {recoveryCodes.map((code, index) => (
                <code key={index} className="recovery-code">
                  {code}
                </code>
              ))}
            </div>

            <button 
              type="button" 
              className="btn btn-secondary btn-full"
              onClick={handleCopyRecoveryCodes}
            >
              {copiedCodes ? '✓ Copied!' : 'Copy all codes'}
            </button>

            <button 
              type="button" 
              className="btn btn-primary btn-full"
              onClick={acknowledgeRecoveryCodes}
            >
              I've saved my codes
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {renderContent()}
      </div>
      
      <div className="login-background">
        <img 
          src="/images/finalformfluhealthylogo.png" 
          alt="" 
          className="background-mascot"
        />
      </div>
    </div>
  );
}

export default LoginPage;
